import React, { useEffect, useState } from "react";
import PortalAdminLayout from "./PortalAdminLayout";
import { useDemoMode } from "../../contexts/DemoModeContext";
import "./DemoDataReset.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

function DemoDataReset() {
  const { isDemoMode } = useDemoMode();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [resultMessage, setResultMessage] = useState("");
  const [backupInfo, setBackupInfo] = useState(null);

  const apiBaseUrl = (API_BASE_URL || "").toLowerCase();
  const isDemoEnvironment =
    !apiBaseUrl ||
    apiBaseUrl.includes("api-demo") ||
    apiBaseUrl.includes("localhost") ||
    apiBaseUrl.includes("127.0.0.1");
  const canUseFeature = isDemoMode && isDemoEnvironment;

  const getToken = () => localStorage.getItem("ogestyle_token") || "";

  const loadDryRun = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/demo/purge-data/dry-run`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Failed to load purge report");
      }

      setReport(data.report?.plan || null);
      if (!data.report?.backup) {
        setBackupInfo(null);
      }
    } catch (err) {
      setError(err.message || "Failed to load purge report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canUseFeature) {
      loadDryRun();
    } else {
      setLoading(false);
    }
  }, [canUseFeature]);

  const handleExecute = async () => {
    const typed = window.prompt(
      "Type PURGE DEMO DATA to confirm deleting demo data:",
      "",
    );

    if (typed !== "PURGE DEMO DATA") {
      return;
    }

    setRunning(true);
    setResultMessage("");
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/demo/purge-data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ confirmation: typed }),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Failed to purge demo data");
      }

      setResultMessage(
        `Demo data purge completed. Deleted ${data.report?.plan?.totalToDelete || 0} document(s).`,
      );
      setReport(data.report?.plan || null);
      setBackupInfo(data.report?.backup || null);
    } catch (err) {
      setError(err.message || "Failed to purge demo data");
    } finally {
      setRunning(false);
      await loadDryRun();
    }
  };

  const plannedDeletions = Array.isArray(report?.deletions) ? report.deletions : [];
  const plannedPreserved = Array.isArray(report?.preserved) ? report.preserved : [];

  const linkedPreservedCollections = plannedPreserved.filter((entry) => entry?.mode === "filtered");
  const fullyDeletedCollections = plannedDeletions.filter((entry) => {
    const filterKeys = Object.keys(entry?.filter || {});
    return filterKeys.length === 0;
  });
  const partiallyDeletedCollections = plannedDeletions.filter((entry) => {
    const filterKeys = Object.keys(entry?.filter || {});
    return filterKeys.length > 0;
  });

  return (
    <PortalAdminLayout>
      <div className="demo-reset-page">
        <h1>Demo Data Reset</h1>
        <p className="demo-reset-subtitle">
          Purge all non-platform demo data while keeping configured preserved test users and platform settings.
        </p>

        {!canUseFeature && (
          <div className="demo-reset-alert danger">
            This feature is available only in demo mode and is disabled in production.
          </div>
        )}

        {error && <div className="demo-reset-alert danger">{error}</div>}
        {resultMessage && <div className="demo-reset-alert success">{resultMessage}</div>}
        {backupInfo?.backupDir && (
          <div className="demo-reset-alert info">
            Automatic backup created before purge.
            <br />
            <strong>Backup folder:</strong> {backupInfo.backupDir}
            <br />
            <strong>Exported docs:</strong> {backupInfo.exportedDocuments || 0}
          </div>
        )}

        {canUseFeature && (
          <>
            <div className="demo-reset-panel">
              <h2>Dry-Run Summary</h2>
              {loading ? (
                <p>Loading report...</p>
              ) : (
                <>
                  <p>
                    <strong>Collections scanned:</strong> {report?.scannedCollections || 0}
                  </p>
                  <p>
                    <strong>Total to delete:</strong> {report?.totalToDelete || 0} document(s)
                  </p>
                  <p>
                    <strong>Preserved docs:</strong> {report?.totalPreserved || 0}
                  </p>
                </>
              )}
            </div>

            <div className="demo-reset-alert warning">
              <strong>Pre-Execution Safety Check</strong>
              <p>
                This action will delete records immediately. Verify preserved users and linked collections below before running purge.
              </p>
              <p>
                <strong>Preserved user emails:</strong>{" "}
                {(report?.preserveUserEmails || []).length > 0
                  ? report.preserveUserEmails.join(", ")
                  : "None configured"}
              </p>
              <p>
                <strong>Linked collections preserved (filtered keep):</strong> {linkedPreservedCollections.length}
              </p>
              <p>
                <strong>Collections fully deleted:</strong> {fullyDeletedCollections.length}
              </p>
            </div>

            <div className="demo-reset-panel">
              <h2>Linked Collections Preserved</h2>
              {linkedPreservedCollections.length === 0 ? (
                <p>No filtered keep collections detected.</p>
              ) : (
                <ul>
                  {linkedPreservedCollections.map((item) => (
                    <li key={item.collection}>
                      {item.collection}: {item.count} preserved
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="demo-reset-panel">
              <h2>Collections Fully Deleted</h2>
              {fullyDeletedCollections.length === 0 ? (
                <p>None.</p>
              ) : (
                <ul>
                  {fullyDeletedCollections.map((item) => (
                    <li key={item.collection}>
                      {item.collection}: {item.count}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="demo-reset-panel">
              <h2>Collections Partially Deleted</h2>
              {partiallyDeletedCollections.length === 0 ? (
                <p>Nothing to delete.</p>
              ) : (
                <ul>
                  {partiallyDeletedCollections.map((item) => (
                    <li key={item.collection}>
                      {item.collection}: {item.count}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="demo-reset-actions">
              <button
                className="demo-reset-btn secondary"
                type="button"
                onClick={loadDryRun}
                disabled={running || loading}
              >
                Refresh Dry-Run
              </button>
              <button
                className="demo-reset-btn danger"
                type="button"
                onClick={handleExecute}
                disabled={running || loading}
              >
                {running ? "Purging..." : "Purge Demo Data"}
              </button>
            </div>
          </>
        )}
      </div>
    </PortalAdminLayout>
  );
}

export default DemoDataReset;
